import React, { useState, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Sparkles, AlertCircle, Info } from 'lucide-react';

/**
 * AttributeRenderer - Dynamic form fields based on taxonomy
 * 
 * This component fetches attributes from the taxonomy API based on
 * the listing type and category, then renders appropriate form fields.
 * 
 * @param {string} listingType - Type of listing (SERVICE, RENTAL, EVENT, PRODUCT, etc.)
 * @param {string} categoryId - Category ID for more specific attributes
 * @param {string} categoryName - Category name for fuzzy matching in Shopify taxonomy
 * @param {string} subcategoryName - Subcategory name for specific attributes (e.g., "Deity Dress")
 * @param {string} listingTitle - Listing title to help match taxonomy
 * @param {object} values - Current values for the attributes
 * @param {function} onChange - Callback when values change: (key, value) => void
 * @param {function} onAttributesLoaded - Callback when attributes are loaded
 * @param {boolean} showCard - Whether to wrap in a Card component
 * @param {string} className - Additional CSS classes
 */
const AttributeRenderer = ({
  listingType,
  categoryId,
  categoryName,
  subcategoryName,
  listingTitle,
  values = {},
  onChange,
  onAttributesLoaded,
  showCard = true,
  className = ''
}) => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('');
  const [taxonomyInfo, setTaxonomyInfo] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch attributes when listing type or category changes
  const fetchAttributes = useCallback(async () => {
    
    if (!listingType) {
      setAttributes([]);
      return;
    }

    // Skip if no category is selected (attributes depend on category)
    if (!categoryId && !categoryName) {
      setAttributes([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = sessionStorage.getItem('token');
      const requestBody = {
        category_id: categoryId || 'default',
        category_name: categoryName || '',
        subcategory_name: subcategoryName || '',
        listing_title: listingTitle || '',
        listing_type: listingType.toUpperCase()
      };
      
      
      const response = await fetch(`${API_URL}/api/taxonomy/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to fetch attributes');
      }

      const data = await response.json();
      
      setAttributes(data.fields || []);
      setSource(data.source || '');
      setTaxonomyInfo({
        category_type: data.category_type,
        suggested_mapping: data.suggested_mapping,
        confidence: data.confidence
      });
      
      if (onAttributesLoaded) {
        onAttributesLoaded(data.fields || [], data);
      }
    } catch (err) {
      console.error('[AttributeRenderer] Error fetching taxonomy attributes:', err);
      setError(err.message);
      setAttributes([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, listingType, categoryId, categoryName, subcategoryName, listingTitle, onAttributesLoaded]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  // Handle value change for an attribute
  const handleChange = (key, value) => {
    if (onChange) {
      onChange(key, value);
    }
  };

  // Render a single attribute field based on its type
  const renderField = (attr) => {
    const value = values[attr.key];
    const fieldId = `attr-${attr.key}`;

    switch (attr.type) {
      case 'text':
        return (
          <div key={attr.key} className="space-y-2">
            <Label htmlFor={fieldId} className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
              {attr.unit && <span className="text-xs text-muted-foreground">({attr.unit})</span>}
            </Label>
            <Input
              id={fieldId}
              value={value || ''}
              onChange={(e) => handleChange(attr.key, e.target.value)}
              placeholder={attr.placeholder || `Enter ${attr.label.toLowerCase()}`}
            />
            {attr.description && (
              <p className="text-xs text-muted-foreground">{attr.description}</p>
            )}
          </div>
        );

      case 'number':
        return (
          <div key={attr.key} className="space-y-2">
            <Label htmlFor={fieldId} className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
              {attr.unit && <span className="text-xs text-muted-foreground">({attr.unit})</span>}
            </Label>
            <Input
              id={fieldId}
              type="number"
              value={value || ''}
              onChange={(e) => handleChange(attr.key, e.target.value ? Number(e.target.value) : '')}
              placeholder={attr.placeholder || `Enter ${attr.label.toLowerCase()}`}
              min={attr.min_value}
              max={attr.max_value}
            />
            {attr.description && (
              <p className="text-xs text-muted-foreground">{attr.description}</p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={attr.key} className="space-y-2">
            <Label htmlFor={fieldId} className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
            </Label>
            <Select
              value={value || ''}
              onValueChange={(v) => handleChange(attr.key, v)}
            >
              <SelectTrigger id={fieldId}>
                <SelectValue placeholder={attr.placeholder || `Select ${attr.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {attr.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {attr.description && (
              <p className="text-xs text-muted-foreground">{attr.description}</p>
            )}
          </div>
        );

      case 'multi_select':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div key={attr.key} className="space-y-2">
            <Label className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
            </Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[42px]">
              {attr.options?.map((option) => {
                const isSelected = selectedValues.includes(option);
                return (
                  <Badge
                    key={option}
                    variant={isSelected ? "default" : "outline"}
                    className="cursor-pointer hover:bg-primary/80"
                    onClick={() => {
                      const newValues = isSelected
                        ? selectedValues.filter(v => v !== option)
                        : [...selectedValues, option];
                      handleChange(attr.key, newValues);
                    }}
                  >
                    {option}
                  </Badge>
                );
              })}
            </div>
            {attr.description && (
              <p className="text-xs text-muted-foreground">{attr.description}</p>
            )}
          </div>
        );

      case 'boolean':
        return (
          <div key={attr.key} className="flex items-center justify-between py-2">
            <div className="space-y-0.5">
              <Label htmlFor={fieldId} className="flex items-center gap-1">
                {attr.label}
                {attr.required && <span className="text-destructive">*</span>}
              </Label>
              {attr.description && (
                <p className="text-xs text-muted-foreground">{attr.description}</p>
              )}
            </div>
            <Switch
              id={fieldId}
              checked={value ?? attr.default_value ?? false}
              onCheckedChange={(checked) => handleChange(attr.key, checked)}
            />
          </div>
        );

      case 'date':
        return (
          <div key={attr.key} className="space-y-2">
            <Label htmlFor={fieldId} className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id={fieldId}
              type="date"
              value={value || ''}
              onChange={(e) => handleChange(attr.key, e.target.value)}
            />
            {attr.description && (
              <p className="text-xs text-muted-foreground">{attr.description}</p>
            )}
          </div>
        );

      case 'datetime':
        return (
          <div key={attr.key} className="space-y-2">
            <Label htmlFor={fieldId} className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id={fieldId}
              type="datetime-local"
              value={value || ''}
              onChange={(e) => handleChange(attr.key, e.target.value)}
            />
            {attr.description && (
              <p className="text-xs text-muted-foreground">{attr.description}</p>
            )}
          </div>
        );

      case 'color':
        return (
          <div key={attr.key} className="space-y-2">
            <Label htmlFor={fieldId} className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={fieldId}
                type="color"
                value={value || '#000000'}
                onChange={(e) => handleChange(attr.key, e.target.value)}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={value || ''}
                onChange={(e) => handleChange(attr.key, e.target.value)}
                placeholder="#000000"
                className="flex-1"
              />
            </div>
            {attr.description && (
              <p className="text-xs text-muted-foreground">{attr.description}</p>
            )}
          </div>
        );

      case 'range':
        return (
          <div key={attr.key} className="space-y-2">
            <Label className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
              {attr.unit && <span className="text-xs text-muted-foreground">({attr.unit})</span>}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={value?.min || ''}
                onChange={(e) => handleChange(attr.key, { ...value, min: e.target.value ? Number(e.target.value) : '' })}
                placeholder="Min"
                min={attr.min_value}
                max={attr.max_value}
                className="flex-1"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="number"
                value={value?.max || ''}
                onChange={(e) => handleChange(attr.key, { ...value, max: e.target.value ? Number(e.target.value) : '' })}
                placeholder="Max"
                min={attr.min_value}
                max={attr.max_value}
                className="flex-1"
              />
            </div>
            {attr.description && (
              <p className="text-xs text-muted-foreground">{attr.description}</p>
            )}
          </div>
        );

      default:
        // Fallback to text input
        return (
          <div key={attr.key} className="space-y-2">
            <Label htmlFor={fieldId} className="flex items-center gap-1">
              {attr.label}
              {attr.required && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id={fieldId}
              value={value || ''}
              onChange={(e) => handleChange(attr.key, e.target.value)}
              placeholder={attr.placeholder || `Enter ${attr.label.toLowerCase()}`}
            />
          </div>
        );
    }
  };

  // Don't render anything if no listing type
  if (!listingType) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading attributes...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`flex items-center gap-2 text-destructive py-4 ${className}`}>
        <AlertCircle className="h-5 w-5" />
        <span>Failed to load attributes: {error}</span>
      </div>
    );
  }

  // No attributes - show info for products, hide for others
  if (attributes.length === 0) {
    // For products, show category match info if available
    if (listingType.toUpperCase() === 'PRODUCT' && source) {
      return null; // Hide for now until we have attributes
    }
    return null;
  }

  // Separate required and optional attributes
  const requiredAttrs = attributes.filter(a => a.required);
  const optionalAttrs = attributes.filter(a => !a.required);

  const content = (
    <div className="space-y-6">
      {/* Source indicator */}
      {source && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="h-3 w-3" />
          <span>Attributes from: {source}</span>
        </div>
      )}

      {/* Required attributes */}
      {requiredAttrs.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {requiredAttrs.map(renderField)}
          </div>
        </div>
      )}

      {/* Optional attributes */}
      {optionalAttrs.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Optional attributes for better search & filtering</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {optionalAttrs.map(renderField)}
          </div>
        </div>
      )}
    </div>
  );

  if (showCard) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {listingType.charAt(0) + listingType.slice(1).toLowerCase()} Attributes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {content}
        </CardContent>
      </Card>
    );
  }

  return <div className={className}>{content}</div>;
};

export default AttributeRenderer;
